import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function PaymentResult({ variant }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const errandId = searchParams.get('errand_id');
  const [status, setStatus] = useState(variant === 'success' ? 'checking' : variant);
  const [errand, setErrand] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const check = useCallback(async () => {
    if (!errandId) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/payments/yoco/status/${errandId}`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setErrand(data);
        if (data.payment_status === 'paid') {
          setStatus('paid');
        }
      }
    } catch (err) { /* silent */ }
  }, [errandId]);

  useEffect(() => {
    if (variant !== 'success') return;
    check();
    const interval = setInterval(() => {
      setAttempts(a => a + 1);
      check();
    }, 2000);
    return () => clearInterval(interval);
  }, [variant, check]);

  useEffect(() => {
    if (attempts >= 10 && status === 'checking') {
      setStatus('pending');
    }
  }, [attempts, status]);

  const config = {
    checking: {
      icon: <Loader2 className="w-16 h-16 text-yellow-500 animate-spin" strokeWidth={1.5} />,
      title: 'Confirming Payment',
      message: 'Waiting for Yoco to confirm your payment...',
      color: 'text-slate-900'
    },
    paid: {
      icon: <CheckCircle className="w-16 h-16 text-emerald-500" strokeWidth={1.5} />,
      title: 'Payment Successful',
      message: `Your errand${errand ? ` for R${errand.price.toFixed(2)}` : ''} is now published and available for runners.`,
      color: 'text-emerald-600'
    },
    pending: {
      icon: <AlertCircle className="w-16 h-16 text-amber-500" strokeWidth={1.5} />,
      title: 'Payment Processing',
      message: 'Your payment is being verified. This can take a minute. Check your dashboard shortly.',
      color: 'text-amber-600'
    },
    cancel: {
      icon: <XCircle className="w-16 h-16 text-slate-400" strokeWidth={1.5} />,
      title: 'Payment Cancelled',
      message: 'You cancelled the payment. Your errand is still saved as unpaid - you can complete payment anytime.',
      color: 'text-slate-600'
    },
    failure: {
      icon: <XCircle className="w-16 h-16 text-red-500" strokeWidth={1.5} />,
      title: 'Payment Failed',
      message: 'The payment could not be processed. Please try again or use a different card.',
      color: 'text-red-600'
    }
  };

  const c = config[status] || config.checking;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white flex items-center justify-center px-4">
      <Card data-testid={`payment-result-${status}`} className="w-full max-w-md bg-white border border-slate-100 shadow-lg rounded-xl p-8 text-center">
        <img src="/logo.png" alt="Errands Boy" className="h-16 w-16 object-contain mx-auto mb-6" />
        <div className="flex justify-center mb-4">{c.icon}</div>
        <h1 className={`text-2xl font-bold mb-3 ${c.color}`}>{c.title}</h1>
        <p className="text-slate-600 mb-6 leading-relaxed">{c.message}</p>
        <div className="flex flex-col gap-2">
          <Button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/customer')}
            className="bg-slate-900 hover:bg-black text-white rounded-full py-3 font-medium btn-hover"
          >
            Back to Dashboard
          </Button>
          {(status === 'cancel' || status === 'failure') && (
            <Button
              data-testid="try-again-btn"
              onClick={() => navigate('/customer')}
              className="bg-yellow-400 hover:bg-yellow-500 text-slate-900 rounded-full py-3 font-bold btn-hover"
            >
              Try Again
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
